// Pagina locale "Corso Samaritani · {Città}" — landing GEO-targeted per sede.
// Mostra solo le date di quella sede, mappa, indirizzo, FAQ.
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Clock3, Users2, Award, ShieldCheck, MapPin, Phone, Mail, Calendar } from "lucide-react";
import { getCourse, SEDI, HERO_IMAGE } from "@/lib/data";
import SEO, { faqPageSchema } from "@/components/SEO";
import CorsiBoard from "@/components/CorsiBoard";

// Configurazione per-sede: org + sede slug 7Manager + indirizzo, email locale
const SEDE_CONFIG = {
    bellinzona: {
        sedeName: "Bellinzona",
        bookingOrgSlug: "7med",
        bookingSedeSlug: "7med-bellinzona",
        bookingTipoNomeIncludes: "samaritan",
        address: "Via San Gottardo 22",
        zip: "6500 Bellinzona",
        mapsQuery: "7MED+Bellinzona+Via+San+Gottardo+22",
        // Maps embed: usa search per non dover gestire API key
        mapEmbed: "https://www.google.com/maps?q=Via+San+Gottardo+22,+6500+Bellinzona&output=embed",
    },
};

export default function SamaritaniSede({ sede: sedeProp }) {
    const params = useParams();
    const sede = sedeProp || params.sede;
    const config = SEDE_CONFIG[sede];
    const course = getCourse("samaritani");

    if (!config || !course) {
        return <Navigate to="/corsi/samaritani" replace />;
    }

    const cityName = config.sedeName;
    const fullTitle = `Corso Samaritani ${cityName}`;
    const description = `${course.tagline} Sede di ${cityName} (${config.address}, ${config.zip}). ${course.certification}. Iscrizione online con conferma immediata.`;

    const faqLocal = [
        { q: `Dove si svolge il Corso Samaritani a ${cityName}?`, a: `Il corso si svolge presso la sede 7MED ${cityName}, ${config.address}, ${config.zip}. Aula attrezzata con manichini e materiale didattico.` },
        ...(course.faq || []).filter((f) => !/dove si svolge/i.test(f.q)),
    ];

    return (
        <>
            <SEO
                title={`${fullTitle} · 10h · CHF 140`}
                description={description}
                path={`/corso-samaritani-${sede}`}
                keywords={`corso samaritani ${cityName}, primo soccorso ${cityName}, corso patente ${cityName}, samaritani ticino`}
                ogImage={HERO_IMAGE}
                structuredData={faqPageSchema(faqLocal)}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Corsi", path: "/corsi" },
                    { name: "Samaritani", path: "/corsi/samaritani" },
                    { name: cityName, path: `/corso-samaritani-${sede}` },
                ]}
            />

            {/* HERO */}
            <section className="bg-white border-b border-ink/10">
                <div className="container-7m pt-10 lg:pt-14 pb-0">
                    <Link to="/corsi/samaritani" className="text-xs font-mono uppercase tracking-[0.2em] text-ink-muted hover:text-brand" data-testid="back-to-samaritani">
                        ← Tutte le sedi del Corso Samaritani
                    </Link>
                </div>
                <div className="container-7m py-10 lg:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 flex-wrap">
                                <div className="px-3 py-1.5 bg-brand text-white text-[10px] font-mono tracking-wider uppercase">{course.eyebrow}</div>
                                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted flex items-center gap-1.5">
                                    <MapPin className="w-3.5 h-3.5 text-brand" strokeWidth={2} /> Sede di {cityName}
                                </div>
                            </div>
                            <h1 className="mt-5 font-heading font-black text-4xl sm:text-5xl lg:text-7xl tracking-tighter leading-[1.04]" data-testid="page-h1">
                                Corso Samaritani <span className="text-brand">{cityName}</span>
                            </h1>
                            <p className="mt-5 sm:mt-6 text-readable-lg max-w-2xl">
                                {course.tagline} A {cityName}, in <strong>{config.address}</strong>.
                                Riconosciuto USTRA, obbligatorio per la licenza di condurre.
                            </p>
                            <div className="mt-8 flex gap-4 flex-wrap">
                                <a href="#date" className="btn-primary" data-testid="hero-cta-dates">
                                    Vedi prossime date <ArrowRight className="w-4 h-4" />
                                </a>
                                <a href="tel:+41912615199" className="btn-secondary inline-flex items-center gap-2">
                                    <Phone className="w-4 h-4" /> 091 261 51 99
                                </a>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="aspect-[4/3] lg:aspect-[5/6] overflow-hidden bg-ink/5">
                                <img src={HERO_IMAGE} alt={`${fullTitle} — corso primo soccorso a ${cityName}`} className="w-full h-full object-cover" loading="eager" />
                            </div>
                        </div>
                    </div>
                </div>
                {/* Quick specs row */}
                <div className="border-t border-ink/10">
                    <div className="container-7m">
                        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-ink/10">
                            <Spec icon={Clock3} label="Durata" value={course.duration} />
                            <Spec icon={Users2} label="Partecipanti" value={course.groupSize} />
                            <Spec icon={Award} label="Certificazione" value="Riconosciuto USTRA" />
                            <Spec icon={ShieldCheck} label="Prezzo" value={course.price || course.priceNote} highlight />
                        </div>
                    </div>
                </div>
            </section>

            {/* DATE — sede di Bellinzona */}
            <section id="date" className="py-20 lg:py-28 bg-[#FAFAFA] border-t border-ink/10">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10 lg:mb-14">
                        <div className="lg:col-span-7">
                            <div className="label-eyebrow text-brand">Prossime date · {cityName}</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">
                                Scegli la tua data.
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-readable">
                            Solo le date confermate per la sede di {cityName}. Iscrizione online con
                            conferma immediata via email.
                        </p>
                    </div>
                    <CorsiBoard
                        org={config.bookingOrgSlug}
                        sede={config.bookingSedeSlug}
                        tipoNomeIncludes={config.bookingTipoNomeIncludes}
                        hideSedePicker
                    />
                </div>
            </section>

            {/* DOVE SIAMO — mappa + indirizzo */}
            <section className="py-20 lg:py-28 bg-white border-t border-ink/10" data-testid="dove-siamo">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-10">
                        <div className="lg:col-span-7">
                            <div className="label-eyebrow text-brand">Dove siamo</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">
                                7MED {cityName}.
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-readable">
                            La nostra aula attrezzata a {cityName}. Manichini, defibrillatori e tutto
                            il materiale didattico necessario.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
                        {/* MAPPA */}
                        <div className="lg:col-span-7 aspect-[4/3] lg:aspect-[16/10] overflow-hidden bg-ink/5 border border-ink/10">
                            <iframe
                                src={config.mapEmbed}
                                title={`Mappa 7MED ${cityName}`}
                                className="w-full h-full border-0"
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                                data-testid="sede-map"
                            />
                        </div>

                        {/* INFO */}
                        <div className="lg:col-span-5 bg-ink text-white p-8 lg:p-10 flex flex-col">
                            <div className="label-eyebrow !text-brand">Indirizzo</div>
                            <div className="mt-4 font-heading font-bold text-2xl leading-tight">
                                7MED {cityName}
                            </div>
                            <div className="mt-2 text-white/85 text-lg leading-snug">
                                {config.address}<br />
                                {config.zip}
                            </div>

                            <div className="mt-8 pt-8 border-t border-white/15 space-y-4 text-sm">
                                <a href="tel:+41912615199" className="flex items-center gap-3 hover:text-brand transition-colors group">
                                    <Phone className="w-4 h-4 text-brand" strokeWidth={1.8} />
                                    <span className="font-semibold">+41 91 261 51 99</span>
                                </a>
                                <a href="mailto:backoffice@7med.ch" className="flex items-center gap-3 hover:text-brand transition-colors group">
                                    <Mail className="w-4 h-4 text-brand" strokeWidth={1.8} />
                                    <span className="font-semibold">backoffice@7med.ch</span>
                                </a>
                            </div>

                            <a
                                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(config.mapsQuery)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-auto pt-8 inline-flex items-center gap-2 text-brand font-bold text-sm hover:gap-3 transition-all"
                                data-testid="get-directions"
                            >
                                Indicazioni stradali <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* COSA IMPARERAI */}
            {course.topics?.length ? (
                <section className="py-20 lg:py-28 bg-[#FAFAFA] border-t border-ink/10">
                    <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <div className="label-eyebrow text-brand">Contenuti</div>
                            <h2 className="mt-4 font-heading font-black text-4xl lg:text-5xl tracking-tighter leading-tight">Cosa imparerai.</h2>
                            <p className="mt-6 text-ink-muted">{course.topicsIntro}</p>
                        </div>
                        <div className="lg:col-span-7">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                                {course.topics.map((topic, i) => (
                                    <div key={topic} className="bg-white p-5 lg:p-6 flex items-start gap-4">
                                        <span className="font-mono text-xs text-brand mt-1">{String(i + 1).padStart(2, "0")}</span>
                                        <span className="font-heading font-semibold text-base leading-snug flex-1">{topic}</span>
                                        <Check className="w-4 h-4 text-brand flex-shrink-0 mt-1.5" strokeWidth={2.5} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            ) : null}

            {/* BONUS INCLUSI */}
            {course.info?.length ? (
                <section className="py-16 lg:py-20 bg-white">
                    <div className="container-7m">
                        <div className="bg-ink text-white p-8 lg:p-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                            {course.info.map((it) => (
                                <div key={it.label}>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-brand">{it.label}</div>
                                    <p className="mt-3 text-sm text-white/85 leading-relaxed">{it.value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            ) : null}

            {/* FAQ locale */}
            <section className="py-20 lg:py-28 bg-[#FAFAFA] border-t border-ink/10">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-4">
                        <div className="label-eyebrow text-brand">FAQ</div>
                        <h2 className="mt-4 font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight">Domande frequenti su {cityName}.</h2>
                    </div>
                    <div className="lg:col-span-8 space-y-px bg-ink/10">
                        {faqLocal.map((it) => (
                            <details key={it.q} className="group bg-white p-6 lg:p-8" data-testid={`faq-${sede}`}>
                                <summary className="cursor-pointer flex items-center justify-between gap-4 list-none">
                                    <span className="font-heading font-semibold text-lg leading-snug">{it.q}</span>
                                    <span className="text-brand text-2xl font-light leading-none transition-transform group-open:rotate-45">+</span>
                                </summary>
                                <p className="mt-4 text-ink-muted leading-relaxed text-readable">{it.a}</p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* GROUP / PRIVATE NOTE */}
            {course.groupNote ? (
                <section className="py-16 lg:py-20 bg-white border-t border-ink/10">
                    <div className="container-7m">
                        <div className="bg-brand text-white p-8 sm:p-10 lg:p-14 flex flex-col lg:flex-row gap-8 lg:items-center lg:justify-between">
                            <div className="flex-1 max-w-2xl">
                                <h3 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight">{course.groupNote.title}</h3>
                                <p className="mt-4 text-white/90 leading-relaxed">{course.groupNote.text}</p>
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                                <a href="tel:+41912615199" className="inline-flex items-center justify-center gap-2 bg-white text-ink px-6 py-3.5 font-bold text-sm tracking-tight hover:bg-ink hover:text-white transition-colors">
                                    <Phone className="w-4 h-4" /> 091 261 51 99
                                </a>
                                <a href="mailto:backoffice@7med.ch" className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-6 py-3.5 font-bold text-sm tracking-tight hover:bg-white hover:text-brand transition-colors">
                                    Scrivici →
                                </a>
                            </div>
                        </div>
                    </div>
                </section>
            ) : null}

            {/* ALTRE SEDI */}
            <section className="py-16 lg:py-20 bg-white border-t border-ink/10">
                <div className="container-7m">
                    <div className="label-eyebrow text-brand">Altre sedi</div>
                    <h2 className="mt-3 font-heading font-black text-3xl lg:text-4xl tracking-tighter leading-tight">Cerchi un&apos;altra sede?</h2>
                    <p className="mt-4 text-ink-muted max-w-2xl">Il Corso Samaritani è disponibile anche nelle altre città del Ticino.</p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        {SEDI.filter((s) => s.slug !== sede).map((s) => (
                            <Link
                                key={s.slug}
                                to={SEDE_CONFIG[s.slug] ? `/corso-samaritani-${s.slug}` : "/corsi/samaritani"}
                                className="inline-flex items-center gap-2 px-5 py-3 border-2 border-ink/15 hover:border-ink text-ink font-bold text-sm tracking-tight transition-colors"
                                data-testid={`other-sede-${s.slug}`}
                            >
                                <MapPin className="w-4 h-4 text-brand" />
                                {s.city}
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}

function Spec({ icon: Icon, label, value, highlight = false }) {
    return (
        <div className="p-5 lg:p-6 flex items-start gap-4 bg-white">
            <Icon className="w-5 h-5 text-brand flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-ink-muted">{label}</div>
                <div className={`font-bold mt-1 ${highlight ? "text-brand font-heading text-xl" : "text-base"}`}>{value}</div>
            </div>
        </div>
    );
}
